import { Card } from "../../components/ui/card";
import Balance from "../../assets/balnce.png";
import Profit from "../../assets/profit.png";
import WinRate from "../../assets/rate.png";
import ActiveTrades from "../../assets/traders.png";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import Axios from "../../Axios";
import { useState, useEffect } from "react";
import { capitalize } from "../../utils/capitalize";
import "./Dashboard.css";

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return "--";
  const date = new Date(dateTimeString);
  if (isNaN(date.getTime())) return "--";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  const amPmHours = Number(hours) >= 12 ? "PM" : "AM";
  return `${day}-${month}-${year} / ${hours}:${minutes}:${seconds} ${amPmHours}`;
};

// Refined Professional Tooltip for Profit History Chart
const CustomChartTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const val = Number(payload[0].value) || 0;
    return (
      <div
        style={{
          background: "rgba(18, 14, 16, 0.96)",
          border: "1px solid rgba(252, 68, 32, 0.5)",
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.7), 0 0 12px rgba(252, 68, 32, 0.2)",
          borderRadius: "8px",
          padding: "8px 12px",
          color: "#fff",
          backdropFilter: "blur(10px)",
          minWidth: "130px",
        }}
      >
        <div style={{ fontSize: "11px", color: "rgba(252, 68, 32, 0.85)", fontWeight: 600, marginBottom: "3px" }}>
          {label || "Date"}
        </div>
        <div
          style={{
            fontSize: "14px",
            fontWeight: 800,
            color: val >= 0 ? "#ffffff" : "#f6465d",
          }}
        >
          {val >= 0 ? `+$${val.toFixed(2)}` : `-$${Math.abs(val).toFixed(2)}`}
        </div>
      </div>
    );
  }
  return null;
};

export function DashboardPage() {
  const [dashboardData, setDashboardData] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);

  async function getDashboardData() {
    try {
      const token = localStorage.getItem("olympusMons");
      const { data } = await Axios.post(
        "/users/user-dashboard",
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (data?.success) {
        setDashboardData(data.result);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoaded(true);
    }
  }

  useEffect(() => {
    getDashboardData();
  }, []);

  // Format currency with standard thousands separators
  const formatAmount = (val) => {
    const num = Number(val || 0);
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <section className={`dashboard-page-container h-full overflow-y-auto pb-8 ${isLoaded ? 'is-page-ready' : 'page-is-loading'}`}>
      {/* ──────────────────────────────────────────────────────────────────────────
          1. HEADER TITLE
          ────────────────────────────────────────────────────────────────────────── */}
      <div className="dashboard-page-header">
        <h1>Dashboard</h1>
        <p>Overview of your trading performance</p>
      </div>

      {/* ──────────────────────────────────────────────────────────────────────────
          2. FOUR STAT CARDS (Total Balance, Total Profit, Win Rate, Active Trades)
          ────────────────────────────────────────────────────────────────────────── */}
      <section className="dashboard-cards-grid">
        <Card as="article" className="dashboard-card-pro">
          <div className="card-header-line">
            <span className="card-title-text">Total Balance</span>
            <div className="card-icon-container">
              <img src={Balance} alt="Balance" />
            </div>
          </div>
          <div className="card-value-display">
            ${formatAmount(dashboardData?.wallet)}
          </div>
        </Card>

        <Card as="article" className="dashboard-card-pro">
          <div className="card-header-line">
            <span className="card-title-text">Total Profit</span>
            <div className="card-icon-container">
              <img src={Profit} alt="Total Profit" />
            </div>
          </div>
          <div className="card-value-display">
            ${formatAmount(dashboardData?.totalProfit)}
          </div>
        </Card>

        <Card as="article" className="dashboard-card-pro">
          <div className="card-header-line">
            <span className="card-title-text">Win Rate</span>
            <div className="card-icon-container">
              <img src={WinRate} alt="Win Rate" />
            </div>
          </div>
          <div className="card-value-display">
            {Number(dashboardData?.winRate || 0).toFixed(2)}%
          </div>
        </Card>

        <Card as="article" className="dashboard-card-pro">
          <div className="card-header-line">
            <span className="card-title-text">Active Trades</span>
            <div className="card-icon-container">
              <img src={ActiveTrades} alt="Active Trades" />
            </div>
          </div>
          <div className="card-value-display">
            {dashboardData?.activeTrades || 0}
          </div>
        </Card>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────────
          3. 30-DAY PROFIT HISTORY AREA CHART
          ────────────────────────────────────────────────────────────────────────── */}
      <section className="dashboard-chart-wrapper">
        <div className="dashboard-chart-header">
          <h3>30-Day Profit History</h3>
          <span className="chart-period-indicator">30 Days Performance</span>
        </div>
        <div className="chart-canvas-container">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={dashboardData?.profitHistory || []}
              margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
            >
              <defs>
                <linearGradient id="ezProfitGlow" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#FC4420" stopOpacity={0.32} />
                  <stop offset="65%" stopColor="#FC4420" stopOpacity={0.06} />
                  <stop offset="100%" stopColor="#FC4420" stopOpacity={0.0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(252, 68, 32, 0.1)" />
              <XAxis
                dataKey="day"
                stroke="#64748b"
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                tickLine={false}
                axisLine={{ stroke: "rgba(252, 68, 32, 0.2)" }}
              />
              <YAxis
                stroke="#64748b"
                tick={{ fontSize: 11, fill: "#94a3b8" }}
                tickLine={false}
                axisLine={{ stroke: "rgba(252, 68, 32, 0.2)" }}
              />
              <Tooltip content={<CustomChartTooltip />} />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#FC4420"
                strokeWidth={2.5}
                fill="url(#ezProfitGlow)"
                dot={{ stroke: "#FC4420", strokeWidth: 2, r: 2.5, fill: "#0b0e14" }}
                activeDot={{
                  stroke: "#FC4420",
                  strokeWidth: 2.5,
                  r: 5,
                  fill: "#ffffff",
                }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────────────────
          4. RECENT TRADES TABLE
          ────────────────────────────────────────────────────────────────────────── */}
      <section className="dashboard-trades-wrapper">
        <div className="dashboard-trades-header">
          <h2>Recent Trades</h2>
        </div>
        <div className="trades-table-scroll-container">
          <table className="pro-trades-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Pair</th>
                <th>Direction</th>
                <th>Entry / Exit</th>
                <th>Amount</th>
                <th>Result</th>
                <th>Profit</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {dashboardData?.last5Trades?.length > 0 ? (
                dashboardData.last5Trades.map((trade, index) => {
                  const isWon = trade.result === "won";
                  const isHigh =
                    String(trade.direction).toLowerCase() === "high" ||
                    String(trade.direction).toLowerCase() === "up";
                  return (
                    <tr
                      key={trade._id || index}
                      className="dashboard-row-animate"
                      style={{ animationDelay: `${index * 0.04}s` }}
                    >
                      <td>{index + 1}</td>
                      <td style={{ fontWeight: 700, color: "#ffffff" }}>
                        {trade.pair?.replace("_", "/")}
                      </td>
                      <td>
                        <span className={isHigh ? "direction-tag-high" : "direction-tag-low"}>
                          {capitalize(trade.direction || "")}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: "12px", color: "#e2e8f0" }}>
                          {trade.start_price || "--"}
                        </div>
                        <div style={{ fontSize: "11px", color: "#64748b" }}>
                          {trade.end_price || "--"}
                        </div>
                      </td>
                      <td style={{ fontWeight: 600, color: "#f8fafc" }}>
                        ${formatAmount(trade.amount)}
                      </td>
                      <td>
                        <span className={isWon ? "result-tag-win" : "result-tag-loss"}>
                          {isWon ? "Win" : capitalize(trade.result || "loss")}
                        </span>
                      </td>
                      <td>
                        <span className={isWon ? "profit-text-win" : "profit-text-loss"}>
                          {isWon ? `+${trade.profit}` : trade.profit}
                        </span>
                      </td>
                      <td style={{ fontSize: "11px", color: "#7e8b9b" }}>
                        {formatDateTime(trade?.createdAt)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="trades-empty-cell">
                    No trades found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
